import MuiTextField, { OutlinedTextFieldProps, StandardTextFieldProps } from '@mui/material/TextField';
import * as React from 'react';
import { Control, Controller, FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import { useHandleMaskedChange } from './hooks';
import { ITextFieldMask, TTextFieldAllowedChars } from './types';

export interface IOutlinedTextField<T extends FieldValues> extends Omit<OutlinedTextFieldProps, 'variant'> {
  dataTestId?: string;
  name: FieldPath<T>;
  hasTopAndBottomMargin?: boolean;
  disableHelperText?: boolean;
  mask?: ITextFieldMask;
  allowedChars?: TTextFieldAllowedChars;
  storeMaskValue?: boolean;
}

export interface IStandardTextField<T extends FieldValues> extends Omit<StandardTextFieldProps, 'variant'> {
  name: FieldPath<T>;
  hasTopAndBottomMargin?: boolean;
  mask?: ITextFieldMask;
  allowedChars?: TTextFieldAllowedChars;
  storeMaskValue?: boolean;
}

export interface IOutlinedTextFieldWithControl<T extends FieldValues> extends IOutlinedTextField<T> {
  control?: Control<T>;
}

export function OutlinedTextField<T extends FieldValues>({ name, ...other }: IOutlinedTextField<T>): React.JSX.Element;
/** @deprecated primary use component without control (control is acquired from context) */
export function OutlinedTextField<T extends FieldValues>({
  control,
  name,
  ...other
}: IOutlinedTextFieldWithControl<T>): React.JSX.Element;

export function OutlinedTextField<T extends FieldValues>({
  allowedChars,
  control,
  dataTestId,
  disableHelperText,
  hasTopAndBottomMargin: spaced,
  mask,
  name,
  storeMaskValue,
  sx,
  ...other
}: IOutlinedTextFieldWithControl<T>): React.JSX.Element {
  const { control: contextControl } = useFormContext<T>();
  const handleMaskedChange = useHandleMaskedChange<T>(mask, !!storeMaskValue, allowedChars);
  const shouldHandleChange = !!mask || !!allowedChars;

  return (
    <Controller
      control={control ?? contextControl}
      name={name}
      render={({ field, fieldState }) => {
        const fieldValue = field.value ?? '';
        const displayValue = mask ? mask.format(mask.parse(String(fieldValue))) : fieldValue;
        const hasExplicitError = typeof other.error === 'boolean';
        const hasError = hasExplicitError ? other.error : !!fieldState?.error;
        const helperText = hasExplicitError
          ? (other.helperText ?? ' ')
          : fieldState?.error?.message || other.helperText || ' ';
        const htmlInputSlotProps =
          typeof other.slotProps?.htmlInput === 'object' ? other.slotProps.htmlInput : undefined;
        const formHelperTextSlotProps =
          typeof other.slotProps?.formHelperText === 'object' ? other.slotProps.formHelperText : undefined;

        return (
          <MuiTextField
            {...other}
            {...field}
            slotProps={{
              ...other.slotProps,
              formHelperText: {
                ...formHelperTextSlotProps,
                'data-testid': 'textfield-helper-text',
              } as unknown as React.HTMLAttributes<HTMLParagraphElement>,
              ...(dataTestId &&
                !other.select && {
                  htmlInput: {
                    ...htmlInputSlotProps,
                    'data-testid': dataTestId,
                  },
                }),
              ...(dataTestId &&
                other.select && {
                  select: {
                    'data-testid': dataTestId,
                  } as Record<string, unknown>,
                }),
            }}
            onChange={shouldHandleChange ? (e) => handleMaskedChange(e, field) : field.onChange}
            value={displayValue}
            disabled={other.disabled}
            error={hasError}
            helperText={disableHelperText ? undefined : helperText}
            variant="outlined"
            sx={spaced ? { mb: 1, mt: 2 } : sx}
          />
        );
      }}
    />
  );
}

export function StandardTextField<T extends FieldValues>({
  allowedChars,
  mask,
  name,
  storeMaskValue,
  ...other
}: IStandardTextField<T>): React.JSX.Element {
  const { control } = useFormContext<T>();
  const handleMaskedChange = useHandleMaskedChange<T>(mask, !!storeMaskValue, allowedChars);
  const shouldHandleChange = !!mask || !!allowedChars;
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const fieldValue = field.value ?? '';
        const displayValue = mask ? mask.format(mask.parse(String(fieldValue))) : fieldValue;

        return (
          <MuiTextField
            {...other}
            {...field}
            onChange={shouldHandleChange ? (e) => handleMaskedChange(e, field) : field.onChange}
            value={displayValue}
            disabled={other.disabled}
            error={!!fieldState?.error}
            helperText={fieldState?.error?.message || other.helperText || ' '}
            variant="standard"
          />
        );
      }}
    />
  );
}

export default OutlinedTextField;
